import React from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import { useCartStore } from '../store/cartStore'
import * as FiIcons from 'react-icons/fi'
import SafeIcon from '../components/ui/SafeIcon'

const { FiTrash2, FiMinus, FiPlus, FiShoppingBag, FiArrowRight } = FiIcons

function CartPage() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { items, removeItem, updateQuantity, getTotalPrice, clearCart } = useCartStore()
  
  const total = getTotalPrice()

  const getLocalized = (obj) => {
    if (!obj) return ''
    if (typeof obj === 'string') return obj
    return obj[i18n.language] || obj['en'] || obj['de'] || ''
  }

  if (items.length === 0) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 p-4">
        <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-200 text-center max-w-md w-full">
          <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <SafeIcon icon={FiShoppingBag} className="w-10 h-10 text-gray-400" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Ihr Warenkorb ist leer</h2>
          <p className="text-gray-600 mb-8">Entdecken Sie Millionen von Produkten zu Fabrikpreisen.</p>
          <Link 
            to="/search" 
            className="block w-full bg-[#FF6600] text-white font-bold py-3 rounded-lg hover:bg-[#e55c00] transition-colors"
          >
            Jetzt einkaufen
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <Helmet>
        <title>{t('nav.cart')} | 1688Euro.com</title>
      </Helmet>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">{t('nav.cart')} ({items.length})</h1>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Cart Items */}
          <div className="flex-1">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-4 border-b border-gray-200 bg-gray-50 hidden sm:flex text-sm font-bold text-gray-500">
                <div className="flex-1">Produkt</div>
                <div className="w-32 text-center">Preis</div>
                <div className="w-32 text-center">Menge</div>
                <div className="w-32 text-center">Gesamt</div>
                <div className="w-16"></div>
              </div>

              {items.map(item => (
                <div key={item.id} className="p-4 border-b border-gray-100 last:border-0 flex flex-col sm:flex-row items-center gap-4">
                  {/* Product Info */}
                  <div className="flex-1 flex gap-4 w-full">
                    <div className="w-20 h-20 bg-gray-100 rounded border border-gray-200 flex-shrink-0">
                      <img src={item.image} alt={getLocalized(item.name)} className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <Link to={`/product/${item.id}`}>
                        <h3 className="text-sm font-medium text-gray-900 hover:text-[#FF6600] hover:underline line-clamp-2 mb-1">
                          {getLocalized(item.name)}
                        </h3>
                      </Link>
                      <div className="text-xs text-gray-500">
                        MOQ: {item.minOrderQuantity} | {item.supplier?.name}
                      </div>
                    </div>
                  </div>

                  {/* Price */}
                  <div className="w-full sm:w-32 text-center font-medium text-gray-900">
                    {item.price.toFixed(2)} €
                  </div>

                  {/* Quantity */}
                  <div className="w-full sm:w-32 flex justify-center">
                    <div className="flex items-center border border-gray-300 rounded-md">
                      <button 
                        onClick={() => updateQuantity(item.id, item.quantity - 1)}
                        className="p-2 hover:bg-gray-100 text-gray-600"
                        disabled={item.quantity <= 1}
                      >
                        <SafeIcon icon={FiMinus} className="w-3 h-3" />
                      </button>
                      <input 
                        type="number" 
                        value={item.quantity}
                        onChange={(e) => updateQuantity(item.id, parseInt(e.target.value) || 1)}
                        className="w-12 text-center text-sm focus:outline-none"
                      />
                      <button 
                        onClick={() => updateQuantity(item.id, item.quantity + 1)}
                        className="p-2 hover:bg-gray-100 text-gray-600"
                      >
                        <SafeIcon icon={FiPlus} className="w-3 h-3" />
                      </button>
                    </div>
                  </div>

                  {/* Total */}
                  <div className="w-full sm:w-32 text-center font-bold text-[#FF6600]">
                    {(item.price * item.quantity).toFixed(2)} €
                  </div>

                  {/* Remove */}
                  <div className="w-full sm:w-16 flex justify-center sm:justify-end">
                    <button 
                      onClick={() => removeItem(item.id)}
                      className="text-gray-400 hover:text-red-500 transition-colors"
                      title="Entfernen"
                    >
                      <SafeIcon icon={FiTrash2} className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="mt-4 flex justify-end">
              <button 
                onClick={clearCart}
                className="text-red-600 text-sm hover:underline"
              >
                Warenkorb leeren
              </button>
            </div>
          </div>

          {/* Summary */}
          <div className="w-full lg:w-80 flex-shrink-0">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 sticky top-24">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Zusammenfassung</h2>
              
              <div className="space-y-3 mb-6">
                <div className="flex justify-between text-gray-600">
                  <span>Zwischensumme</span>
                  <span>{total.toFixed(2)} €</span>
                </div>
                <div className="flex justify-between text-gray-600">
                  <span>Versand</span>
                  <span className="text-xs text-gray-400">(Wird beim Checkout berechnet)</span>
                </div>
                <div className="border-t border-gray-200 pt-3 flex justify-between font-bold text-lg text-gray-900">
                  <span>Gesamt</span>
                  <span>{total.toFixed(2)} €</span>
                </div>
              </div>

              <div className="space-y-3">
                <button 
                  onClick={() => navigate('/checkout')}
                  className="w-full bg-[#FF6600] text-white font-bold py-3 px-4 rounded-lg hover:bg-[#e55c00] transition-colors flex items-center justify-center"
                >
                  <span>Zur Kasse</span>
                  <SafeIcon icon={FiArrowRight} className="w-4 h-4 ml-2" />
                </button>
                
                <div className="text-xs text-gray-500 text-center mt-4">
                  <p className="mb-2">Sichere Bezahlung mit:</p>
                  <div className="flex justify-center gap-2 grayscale opacity-70">
                    <div className="bg-gray-200 px-2 py-1 rounded">VISA</div>
                    <div className="bg-gray-200 px-2 py-1 rounded">Mastercard</div>
                    <div className="bg-gray-200 px-2 py-1 rounded">PayPal</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CartPage